import moment from 'moment';
import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  NativeSyntheticEvent,
  StyleProp,
  StyleSheet,
  TextInputFocusEventData,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { View } from '@shared/components/View';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextBody1Bold, TextAlign } from '@shared/components/typography';
import { ColorThemeContext, Colors, grid } from '@shared/styles';

export interface FocusRef {
  focus(): void;
}

interface Props {
  label: string;
  icon?: MBIconV2Name;
  value: string;
  style?: StyleProp<ViewStyle>;
  textAlign?: TextAlign;
  allowCustomInput?: boolean;
  formats: string[];
  highlight?: boolean;
  onChange(value: string | undefined): void;
  onBlur?(): void;
  onFocus?(): void;
  autoFocus?: boolean;
  forwardRef?: MutableRefObject<FocusRef | null>;
}

export function MBComboDatePickerField({
  label,
  icon,
  formats,
  value: _value,
  style,
  highlight,
  textAlign,
  allowCustomInput = true,
  onChange,
  onBlur,
  onFocus,
  autoFocus,
  forwardRef,
}: Props) {
  const [value, setValue] = useState(_value || '');
  useEffect(() => setValue(_value || ''), [_value]);

  const colors = useContext(ColorThemeContext);
  const [active, setActive] = useState(false);
  const showTextInput = active && allowCustomInput;
  const clearButtonRef = useRef<TouchableOpacity>(null);
  const textInputRef = useRef<MBTextInput>(null);
  const focusTimeoutRef = useRef<Timeout | null>(null);

  const focus = useCallback(() => {
    setActive(true);
    onFocus?.();
    // Android Hack: Focus must happen some time after the state changes to work correctly
    focusTimeoutRef.current = setTimeout(() => {
      textInputRef.current?.focus();
      focusTimeoutRef.current = null;
    }, 100);
  }, [onFocus]);

  if (forwardRef) {
    // Expose the real focus method so parents can programmatically focus this field
    forwardRef.current = { focus };
  }

  useEffect(() => {
    if (autoFocus) {
      focus();
    }
  }, [autoFocus, focus]);

  const clear = useCallback(() => {
    // if the input is empty, clicking the clear button has the same effect as blurring
    if (value === '') {
      setActive(false);
      onBlur?.();
    } else {
      onChange(undefined);
      textInputRef.current?.focus();
    }
  }, [onChange, value, onBlur]);

  const blur = useCallback(
    (event: NativeSyntheticEvent<TextInputFocusEventData>) => {
      // Cancel any pending delayed focus so the input doesn't steal focus again while scrolling
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }
      if ((event?.nativeEvent as any)?.relatedTarget === clearButtonRef.current) {
        // this blur event originated from clicking the clear button
        // in this specific scenario, we do not want to deactivate but allow the clear callback to run
        return;
      }

      const parsed = moment(value, formats);
      if (value !== _value && parsed.isValid()) {
        onChange(parsed.toISOString());
      } else {
        setValue(_value);
      }

      setActive(false);
      onBlur?.();
    },
    [onBlur, value, _value, formats, onChange],
  );

  const handleChange = useCallback(
    (text: string) => {
      setValue(text);
      // strict validation when typing
      const parsed = moment(text, formats, true);
      if (text !== _value && parsed.isValid()) {
        onChange(parsed.toISOString());
      }
    },
    [formats, _value, onChange],
  );

  return (
    <TouchableOpacity
      style={[styles.field, (showTextInput || highlight) && styles.active, style]}
      disabled={showTextInput}
      onPress={focus}
    >
      {!!icon && (
        <MBIconV2
          name={icon}
          size={grid(4)}
          color={active || Boolean(value) ? colors.accent : colors.input.label}
        />
      )}
      <View paddingRight={0.5} />
      {showTextInput ? (
        <MBTextInput
          ref={textInputRef}
          value={value}
          onChangeText={handleChange}
          noPadding
          noBorder
          selectTextOnFocus
          textContentType="none"
          autoCorrect={false}
          placeholder={label}
          onBlur={blur}
          keyboardType={'numbers-and-punctuation' as any}
          align={!active && Boolean(value) ? 'right' : 'left'}
          style={{ flex: 1, minWidth: 0 }}
        />
      ) : Boolean(value) ? (
        <View flex>
          <MBTextBody1 align={textAlign} numberOfLines={1} color={colors.foregroundActive}>
            {value}
          </MBTextBody1>
        </View>
      ) : (
        <View flex>
          <MBTextBody1Bold align={textAlign} color={colors.foregroundActive}>
            {label}
          </MBTextBody1Bold>
        </View>
      )}

      {(showTextInput || (highlight && !!value)) && (
        <TouchableOpacity
          ref={clearButtonRef}
          onPress={clear}
          style={{
            width: grid(4),
            height: grid(4),
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <MBIconV2 name="exit-small" color={colors.foregroundInactive} size={grid(2)} />
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  field: {
    padding: grid(0.5),
    flexDirection: 'row',
    alignItems: 'center',
  },
  active: {
    backgroundColor: Colors.v2.gray05,
    borderRadius: grid(1),
  },
});
